/**
 * The action bar's ONE decision (fix D / R5): which FACE it wears.
 *
 * It folds in M1a's `Toolbar/model.ts` (`watching`), because the bar has a third
 * face the toolbar did not: once the game is over the two destructive actions go
 * away and Rematch / New game take the slot, which is the thing the owner kept
 * leaving the screen for.
 *
 * ONE `useShallow` object of two booleans — neither of them moves per move.
 */
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';
import {selectBoardSource, selectGameOver} from '@/Store/liveGame/selectors';

/** live: Draw/Resign/Abort · watch: Stop watching · over: Rematch/New game */
export type ActionBarFace = 'live' | 'watch' | 'over';

export const selectActionBar = (
  s: LiveGameState,
): {watching: boolean; over: boolean} => ({
  watching: selectBoardSource(s) === 'watch',
  over: selectGameOver(s),
});

export const faceFor = (watching: boolean, over: boolean): ActionBarFace =>
  watching ? 'watch' : over ? 'over' : 'live';

export type ActionBarModel = {
  face: ActionBarFace;
};

export function useActionBarModel(): ActionBarModel {
  const {watching, over} = useLiveGame(useShallow(selectActionBar));
  return {face: faceFor(watching, over)};
}

export default useActionBarModel;
